import React, { useState, useEffect } from "react";
import Chart from "react-google-charts";
import ReactApexChart from "react-apexcharts";
import {
  Card,
  CardBody,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  CardHeader,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import CardHeaderSpan from "../../Utils/CommonComponents/CardHeaderSpan";
import "./AllReports.css";
import {
  googleChartData,
  comboChartData,
  comboChartOption,
} from "../../Data/ChartsData/GoogleChartData";
import DailyCallAverage from "./AllKPIs/DailyCallAverage";
import RoutesCoverage from "./AllKPIs/RoutesCoverage";
import CustomerCoverage from "./AllKPIs/CustomerCoverage";
import PCPM from "./AllKPIs/PCPM";
import FocusBrandAchievement from "./AllKPIs/FocusBrandAchievement";
import FarmerJagrutiMeeting from "./AllKPIs/FarmerJagrutiMeeting";
import Classification from "./AllKPIs/Classification";
import POB_POD from "./AllKPIs/POB_POD";
import RCPA from "./AllKPIs/RCPA";
import AdministrationDays from "./AllKPIs/AdministrationDays";
import WeeklySalesPattern from "./AllKPIs/WeeklySalesPattern";
import Expense from "./AllKPIs/Expense";
import DemonstrationActivities from "./AllKPIs/DemonstrationActivities";
import DSO from "./AllKPIs/DSO";
import CreditLimit from "./AllKPIs/CreditLimit";
import Invoice from "./AllKPIs/Invoice";
import Payment from "./AllKPIs/Payment";
import ChequeBounce from "./AllKPIs/ChequeBounce";
import Expiry from "./AllKPIs/Expiry";
import SalesReturn from "./AllKPIs/SalesReturn";
import MonthlyEvaluationFormat from "./AllKPIs/MonthlyEvaluationFormat";
import TreeModel from "../Dashboards/Attendance/AttendanceTable/TreeModel";
import { setDateRange } from "../../redux-toolkit/positionSlice";
import { RootState } from "../../redux-toolkit/store"; // Import the RootState type
import { H2, H3, H5, H6, Image, ProgressBar } from "../../AbstractElements";
import SvgIcon from "../../Utils/CommonComponents/CommonIcons/CommonSvgIcons";
import {
  monthlyDropdownList,
  onlineOrderCardData,
  orderOverviewData,
} from "../../Data/DashboardsData/ECommerceData";
import { totalEarningCardsData } from "../../Data/DashboardsData/DefaultData";
import {
  OrderOverviewTitle,
  ThanLastWeek,
  TotalRevenue,
} from "../../Utils/Constants";
import DropdownWithHeader from "../../Utils/CommonComponents/DashboardsCommon/DropdownWithHeader";
import TopSell from "../AllKPIs/TopSell";
import CategorySales from "../AllKPIs/CategorySales";
import { getFilteredPositionIds } from "../../db/DexieServises";
import { API } from "../../api/API";
import { getData } from "../../CommonElements/Functions/PostGetFun";
import { dynamicImage } from "../../Utils";

// Render charts directly without using the CommonChart wrapper
export default function AllReports() {
  const dispatch = useDispatch();
  const monthRangeFromRedux = useSelector(
    (state: RootState) => state.position.dateRange
  );
  const positions = useSelector((state: RootState) => state.position.positions);
  const dateRange = useSelector((state: RootState) => state.position.dateRange);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);

  // Chart configs for selection
  const chartConfigs = [
    {
      id: "dailyCallAverage",
      label: "Daily Call Average",
      render: () => <DailyCallAverage />,
    },
    {
      id: "routesCoverage",
      label: "Routes Coverage",
      render: () => <RoutesCoverage />,
    },
    {
      id: 'customerCoverage',
      label: 'Customer Coverage',
      render: () => <CustomerCoverage />,
    },
    {
      id: 'pcpm',
      label: 'PCPM',
      render: () => <PCPM />,
    },
    {
      id: 'focusBrandAchievement',
      label: 'Focus Brand Achievement',
      render: () => <FocusBrandAchievement />,
    },
    {
      id: 'farmerJagrutiMeeting',
      label: 'Farmer Jagruti Meeting',
      render: () => <FarmerJagrutiMeeting />,
    },
    {
      id: "classification",
      label: "Classification",
      render: () => <Classification />,
    },
    {
      id: 'pobPod',
      label: 'POB / POD',
      render: () => <POB_POD />,
    },
    {
      id: 'rcpa',
      label: 'RCPA',
      render: () => <RCPA />,
    },
    {
      id: 'administrationDays',
      label: 'Administration Days',
      render: () => <AdministrationDays />,
    },
    {
      id: 'weeklySalesPattern',
      label: 'Weekly Sales Pattern',
      render: () => <WeeklySalesPattern />,
    },
    {
      id: 'expense',
      label: 'Expense',
      render: () => <Expense />,
    },
    {
      id: 'demonstrationActivities',
      label: 'Demonstration Activities',
      render: () => <DemonstrationActivities />,
    },
    {
      id: 'dso',
      label: 'DSO',
      render: () => <DSO />,
    },
    {
      id: 'creditLimit',
      label: 'Credit Limit',
      render: () => <CreditLimit />,
    },
    {
      id: 'invoice',
      label: 'Invoice',
      render: () => <Invoice />,
    },
    {
      id: 'payment',
      label: 'Payment',
      render: () => <Payment />,
    },
    {
      id: 'chequeBounce',
      label: 'Cheque Bounce',
      render: () => <ChequeBounce />,
    },
    {
      id: 'expiry',
      label: 'Expiry',
      render: () => <Expiry />,
    },
    {
      id: 'salesReturn',
      label: 'Sales Return',
      render: () => <SalesReturn />,
    },
    {
      id: 'monthlyEvaluationFormat',
      label: 'Monthly Evaluation Format',
      render: () => <MonthlyEvaluationFormat />,
    },
  ];

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [visibleCharts, setVisibleCharts] = useState(
    chartConfigs.map((c) => c.id)
  );
  const [selectedChartIds, setSelectedChartIds] = useState<string[]>(
    chartConfigs.map((c) => c.id)
  );
  const [allSelected, setAllSelected] = useState(true); // Track if all charts are selected
  const [selectedTreeIds, setSelectedTreeIds] = useState<(string | number)[]>(
    []
  );
  const [monthRange, setMonthRange] = useState({
    from: { month: "", year: "" },
    to: { month: "", year: "" },
  });
  const [monthModalOpen, setMonthModalOpen] = useState(false); // Added state for month modal
  const [topSellingFilter, setTopSellingFilter] = useState("default");
  const [topSellingMonthFilter, setTopSellingMonthFilter] = useState("default");
  const [categorySalesFilter, setCategorySalesFilter] = useState("default");
  const [categorySalesMonthFilter, setCategorySalesMonthFilter] =
    useState("default");
  const [topProducts, setTopProducts] = useState([
    { item: "ProtinC", quantity: 618, sales: 214 },
    { item: "AllGone", quantity: 1721, sales: 172 },
    { item: "ProtinC Keto+", quantity: 1206, sales: 121 },
    { item: "ReproPlus Gold", quantity: 776, sales: 85 },
    { item: "Crux LA", quantity: 530, sales: 81 },
    { item: "Hestaeaf CV", quantity: 593, sales: 77 },
    { item: "iHeal N", quantity: 321, sales: 68 },
    { item: "GPV", quantity: 680, sales: 67 },
    { item: "Kleanoxone", quantity: 576, sales: 62 },
    { item: "Decarnitox", quantity: 520, sales: 55 },
  ]);
  const [categorySales, setCategorySales] = useState([
    { name: "Therapeutics", value: 994, target: 1754 },
    { name: "Live Vaccines", value: 164, target: 570 },
    { name: "Inactivated Vaccines", value: 118, target: 167 },
    { name: "Feed Supplements", value: 373, target: 223 },
    { name: "Biosecurity", value: 118, target: 167 },
    { name: "Herbal Products", value: 118, target: 167 },
    { name: "Homeopathic Products", value: 118, target: 167 },
  ]);

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const toggleFilterModal = () => setFilterModalOpen((v) => !v);
  const handleChartToggle = (id: string) => {
    setSelectedChartIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };
  const showAll = () => setVisibleCharts(chartConfigs.map((c) => c.id));
  const hideAll = () => setVisibleCharts([]);
  const toggleSelectAll = () => {
    if (allSelected) {
      setVisibleCharts([]);
    } else {
      setVisibleCharts(chartConfigs.map((c) => c.id));
    }
    setAllSelected(!allSelected);
    toggleFilterModal(); // Close the modal after toggling select all/deselect all
  };
  const toggleMonthModal = () => setMonthModalOpen((prev: boolean) => !prev); // Fixed missing state and added type for 'prev'

  const monthToNumber = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
  } as const;

  const monthNumberToName: { [key: number]: string } = {
    1: "Jan",
    2: "Feb",
    3: "Mar",
    4: "Apr",
    5: "May",
    6: "Jun",
    7: "Jul",
    8: "Aug",
    9: "Sep",
    10: "Oct",
    11: "Nov",
    12: "Dec",
  };

  const positionOptions = [
    { label: "All Positions", value: "all" },
    ...positions.map((pos) => ({
      label: pos.name,
      value: pos.name,
    })),
  ];
  const dateRangeOptions =
    dateRange.start && dateRange.end
      ? [
          { label: `From: ${dateRange.start}`, value: dateRange.start },
          { label: `To: ${dateRange.end}`, value: dateRange.end },
        ]
      : [];

  const generateMonthOptions = (start: string, end: string) => {
    const [startMonth, startYear] = start.split("-").map(Number);
    const [endMonth, endYear] = end.split("-").map(Number);

    const months = [{ label: "All Months", value: "all" }];
    for (let year = startYear; year <= endYear; year++) {
      const startM = year === startYear ? startMonth : 1;
      const endM = year === endYear ? endMonth : 12;

      for (let month = startM; month <= endM; month++) {
        months.push({
          label: `${monthNumberToName[month]}-${year}`,
          value: `${month}-${year}`,
        });
      }
    }
    return months;
  };

  const monthOptions =
    dateRange.start && dateRange.end
      ? generateMonthOptions(dateRange.start, dateRange.end)
      : [];

  const handleTopSellingFilterChange = (filter: string) => {
    setTopSellingFilter(filter);
    setTopProducts(
      topProducts.map((prod) => ({
        ...prod,
        quantity: Math.floor(Math.random() * 100),
        sales: Math.floor(Math.random() * 200),
      }))
    );
  };

  const handleTopSellingMonthFilterChange = (filter: string) => {
    setTopSellingMonthFilter(filter);
    setTopProducts(
      topProducts.map((prod) => ({
        ...prod,
        quantity: Math.floor(Math.random() * 100),
        sales: Math.floor(Math.random() * 200),
      }))
    );
  };

  const handleCategorySalesFilterChange = (filter: string) => {
    setCategorySalesFilter(filter);
    setCategorySales(
      categorySales.map((cat) => ({
        ...cat,
        value: Math.floor(Math.random() * cat.target),
      }))
    );
  };

  const handleCategorySalesMonthFilterChange = (filter: string) => {
    setCategorySalesMonthFilter(filter);
    setCategorySales(
      categorySales.map((cat) => ({
        ...cat,
        value: Math.floor(Math.random() * cat.target),
      }))
    );
  };

  const [averageDailyCalls, setAverageDailyCalls] = useState(0);
  const [averagePobValue, setAveragePobValue] = useState(0);
  const [averagePodValue, setAveragePodValue] = useState(0);
  const [averageRcpaOwnValue, setAverageRcpaOwnValue] = useState(0);
  const [averageRcpaCompValue, setAverageRcpaCompValue] = useState(0);
  const [loading, setLoading] = useState(false);

  const formatMonth = (month: string) => {
    const [m, y] = month.split("-").map(Number);
    return `${y}-${m.toString().padStart(2, "0")}`;
  };

  const calculateAverages = async () => {
    setLoading(true); // Start loader
    const positionIds =
      positions.length > 0
        ? positions.map((pos) => pos.id).join(",")
        : (await getFilteredPositionIds("Position", unitId)).join(",");

    const months =
      dateRange.start && dateRange.end
        ? generateMonthOptions(dateRange.start, dateRange.end).map((opt) =>
            formatMonth(opt.value)
          )
        : Array.from({ length: 12 }, (_, i) => {
            const month = (i + 1).toString().padStart(2, "0");
            return `2025-${month}`;
          });

    let totalDailyCalls = 0;
    let totalPobValue = 0;
    let totalPodValue = 0;
    let totalRcpaOwnValue = 0;
    let totalRcpaCompValue = 0;
    let count = 0;

    for (const month of months) {
      const serverUrl = localStorage.getItem("serverUrl");
      const url = `${serverUrl}${API.getEffortReport}`;
      console.log(
        url,
        positionIds,
        month,
        "=============================report top"
      );

      const { status, data }: { status: number; data: { data: any[] } } =
        await getData(url, true, {
          position_id: positionIds,
          moye: month,
        });

      if (status === 200 && Array.isArray(data?.data)) {
        data.data.forEach((item: any) => {
          totalDailyCalls += parseFloat(item.PrimaryDcrAverage || "0");
          totalPobValue += parseFloat(item.PobValue || "0");
          totalPodValue += parseFloat(item.PodValue || "0");
          totalRcpaOwnValue += parseFloat(item.RcpaValueForOwnBrand || "0");
          totalRcpaCompValue += parseFloat(item.RcpaValueForCompBrand || "0");
          count++;
        });
      }
    }

    const avgDailyCalls = count > 0 ? totalDailyCalls / count : 0;
    setAverageDailyCalls(avgDailyCalls);
    setAveragePobValue(totalPobValue);
    setAveragePodValue(totalPodValue);
    setAverageRcpaOwnValue(totalRcpaOwnValue);
    setAverageRcpaCompValue(totalRcpaCompValue);
    setLoading(false); // Stop loader
  };

  useEffect(() => {
    calculateAverages();
  }, [positions, dateRange]);

  const formatValue = (value: number) => {
    if (value >= 1000000000) {
      return `${(value / 1000000000).toFixed(2)}B`;
    } else if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}K`;
    }
    return value.toFixed(2);
  };

  return (
    <div className="powerbi-dashboard-bg py-4 px-2  page-body">
      {loading ? (
        <div className="text-center py-3">
          {/* <span className="spinner-border text-primary" role="status" aria-hidden="true" /> */}
          <div>
            <Image
              className="b-r-10 img-80"
              src={dynamicImage("dashboard/loader.gif")}
              alt="Loading..."
            />
          </div>
        </div>
      ) : (
        <>
          <div className="d-flex">
            <div className="col-lg-3 col-md-4 col-sm-6 me-2">
              <Card
                style={{
                  minHeight: "120px",
                  cursor: "pointer",
                }}
              >
                <CardBody
                  style={{
                    padding: "12px",
                    justifyContent: "space-between",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    className="customer-card d-flex justify-content-between align-items-center b-l-primary border-2"
                    style={{ display: "inline-block" }}
                  >
                    <div className="ms-3">
                      <h6
                        className="mb-1"
                        style={{
                          fontSize: "0.9rem",
                          color: "#888",
                          marginBottom: "4px",
                        }}
                      >
                        Calls Average
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "1rem",
                          margin: 0,
                          fontWeight: 600,
                        }}
                      >
                        {formatValue(averageDailyCalls)}
                      </h3>
                    </div>
                    <div className={`dashboard-user bg-light-danger`}>
                      <span></span>
                      <SvgIcon
                        iconId={"male"}
                        style={{
                          width: "30px",
                          height: "30px",
                          marginRight: "4px",
                        }}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6 me-2">
              <Card
                style={{
                  minHeight: "120px",
                  cursor: "pointer",
                }}
              >
                <CardBody
                  style={{
                    padding: "12px",
                    justifyContent: "space-between",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    className="customer-card d-flex justify-content-between align-items-center b-l-primary border-2"
                    style={{ display: "inline-block" }}
                  >
                    <div className="ms-3">
                      <h6
                        className="mb-1"
                        style={{
                          fontSize: "0.9rem",
                          color: "#888",
                          marginBottom: "4px",
                        }}
                      >
                        POB vs POD
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "1rem",
                          margin: 0,
                          fontWeight: 600,
                        }}
                      >
                        ₹ {formatValue(averagePobValue)}/
                        {formatValue(averagePodValue)}
                      </h3>
                    </div>
                    <div className={`dashboard-user bg-light-danger`}>
                      <span></span>
                      <SvgIcon
                        iconId={"money"}
                        style={{
                          width: "30px",
                          height: "30px",
                          marginRight: "4px",
                        }}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
            <div className="col-lg-3 col-md-4 col-sm-6 me-2">
              <Card
                style={{
                  minHeight: "120px",
                  cursor: "pointer",
                }}
              >
                <CardBody
                  style={{
                    padding: "12px",
                    justifyContent: "space-between",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    className="customer-card d-flex justify-content-between align-items-center b-l-primary border-2"
                    style={{ display: "inline-block" }}
                  >
                    <div className="ms-3">
                      <h6
                        className="mb-1"
                        style={{
                          fontSize: "0.9rem",
                          color: "#888",
                          marginBottom: "4px",
                        }}
                      >
                        RCPA Own vs Competitor
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "1rem",
                          margin: 0,
                          fontWeight: 600,
                        }}
                      >
                        ₹ {formatValue(averageRcpaOwnValue)}/
                        {formatValue(averageRcpaCompValue)}
                      </h3>
                    </div>
                    <div className={`dashboard-user bg-light-danger`}>
                      <span></span>
                      <SvgIcon
                        iconId={"money"}
                        style={{
                          width: "30px",
                          height: "30px",
                          marginRight: "4px",
                        }}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* <div className="col-lg-2 col-md-4 col-sm-6 me-2">
              <Card
                style={{
                  minHeight: "120px",
                  cursor: "pointer",
                }}
              >
                <CardBody
                  style={{
                    padding: "12px",
                    justifyContent: "space-between",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    className="customer-card d-flex justify-content-between align-items-center b-l-primary border-2"
                    style={{ display: "inline-block" }}
                  >
                    <div className="ms-3">
                      <h6
                        className="mb-1"
                        style={{
                          fontSize: "0.9rem",
                          color: "#888",
                          marginBottom: "4px",
                        }}
                      >
                        All Employees
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "1.4rem",
                          margin: 0,
                          fontWeight: 600,
                        }}
                      >
                        150
                      </h3>
                    </div>
                    <div className={`dashboard-user bg-light-danger`}>
                      <span></span>
                      <SvgIcon
                        iconId={"male"}
                        style={{
                          width: "30px",
                          height: "30px",
                          marginRight: "4px",
                        }}
                      />
                    </div>
                  </div>
                  <div
                    className="customer mt-2"
                    style={{
                      fontSize: "0.8rem",
                      color: "#28a745",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <SvgIcon
                      iconId="arrow-up"
                      style={{ width: "18px", height: "18px", marginRight: "4px" }}
                    />
                    <h6>+ 100%</h6>
                  </div>
                </CardBody>
              </Card> 
            </div> */}
          </div>

          {/* <Row className="g-4">
            {chartConfigs
              .filter((c) => visibleCharts.includes(c.id))
              .map((c) => c.render())}
          </Row>
          {visibleCharts.length === 0 && (
            <div className="text-center text-muted py-5">
              No charts selected. Use the filter button to show charts.
            </div>
          )} */}
          {/* Static view: render all charts at the end of the component */}
          <div className="mt-4">
            <h5 className="mb-3">All Charts (Static View)</h5>
            <Row className="g-4">
              {chartConfigs.map((c) => (
                <React.Fragment key={`static-${c.id}`}>
                  {c.render()}
                </React.Fragment>
              ))}
            </Row>
          </div>
        </>
      )}
    </div>
  );
}
